import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Menu, X, User, LogOut, LayoutDashboard, Plus, Package, Bell, FileText, Heart, Sparkles, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export default function Navbar() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-white/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="font-playfair text-2xl font-bold text-gold-gradient">Patina</span>
            <span className="font-manrope text-sm text-muted-foreground tracking-widest uppercase">Mart</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/marketplace"
              className="text-sm font-medium text-muted-foreground hover:text-accent transition-colors tracking-wide uppercase font-manrope"
              data-testid="marketplace-link"
            >
              Browse
            </Link>

            <NavMenuTrigger
              label="Buyers"
              testId="buyers-menu"
              items={[
                { to: '/marketplace',  label: 'Browse Materials' },
                { to: '/trust',        label: 'How buying works' },
                ...(user ? [{ to: '/projects',  label: 'My Projects' }] : []),
                ...(user ? [{ to: '/favorites', label: 'Saved Listings' }] : []),
                ...(user ? [{ to: '/alerts',    label: 'Material Alerts' }] : []),
              ]}
            />

            <NavMenuTrigger
              label="Suppliers"
              testId="suppliers-menu"
              items={[
                { to: '/suppliers',    label: 'Browse Suppliers' },
                { to: '/auth?mode=register', label: 'Become a Supplier' },
                { to: '/trust',        label: 'Trust & Verification' },
                { to: '/supplier-app', label: 'Wingman Mobile App' },
                ...(user?.user_type === 'supplier' ? [{ to: '/dashboard', label: 'Supplier Dashboard' }] : []),
                ...(user?.user_type === 'supplier' ? [{ to: '/trade-exchange', label: 'Trade · Exchange' }] : []),
              ]}
            />

            <NavMenuTrigger
              label="Designers"
              testId="designers-menu"
              items={[
                { to: '/sourcing',     label: 'Sourcing Calls' },
                { to: '/inspiration',  label: 'Inspiration Gallery' },
                { to: '/revit',        label: 'Revit Plugin' },
                { to: '/sketchup',     label: 'SketchUp Plugin' },
                { href: 'mailto:hello@patinamart.com?subject=ArchiCAD%20plugin%20beta', label: 'ArchiCAD (beta)' },
              ]}
            />

            <Link
              to="/about"
              className="text-sm font-medium text-muted-foreground hover:text-accent transition-colors tracking-wide uppercase font-manrope"
              data-testid="about-link"
            >
              About
            </Link>
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {loading ? (
              <div className="w-8 h-8 rounded-full bg-secondary animate-pulse" />
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 border border-accent/30 hover:border-accent/50 hover:bg-accent/10" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full ring-2 ring-accent/50" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-primary flex items-center justify-center ring-2 ring-accent/50">
                        <span className="text-sm font-semibold text-white">{user.name?.charAt(0)}</span>
                      </div>
                    )}
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-semibold text-foreground">{user.name}</span>
                      <span className="text-xs text-accent capitalize">{user.user_type}</span>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-card border-border">
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/alerts')} data-testid="alerts-menu-item">
                    <Bell className="w-4 h-4 mr-2" />
                    Material Alerts
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="favorites-menu-item">
                    <Heart className="w-4 h-4 mr-2" />
                    Saved Listings
                  </DropdownMenuItem>
                  {user.user_type === 'supplier' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="dashboard-menu-item">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/create-listing')} data-testid="create-listing-menu-item">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Listing
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/bulk-import')} data-testid="bulk-import-menu-item">
                        <FileText className="w-4 h-4 mr-2" />
                        Bulk Import
                      </DropdownMenuItem>
                    </>
                  )}
                  {(user.user_type === 'admin' || (user.email && user.email.toLowerCase() === 'thisisradas@gmail.com')) && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin/inspirations')} data-testid="admin-inspirations-menu-item">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Moderate Submissions
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/clip')} data-testid="admin-clip-menu-item">
                        <Plus className="w-4 h-4 mr-2" />
                        Web Clipper
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/auth')}
                  className="text-sm"
                  data-testid="login-button"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/auth?mode=register')}
                  className="btn-gold text-sm px-6"
                  data-testid="register-button"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-muted-foreground"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/20">
            <div className="flex flex-col gap-2">
              <Link
                to="/marketplace"
                className="text-sm font-medium text-muted-foreground hover:text-accent py-2"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-browse"
              >
                Browse Marketplace
              </Link>

              <MobileSection title="For buyers" testId="mobile-buyers" items={[
                { to: '/marketplace', label: 'Browse Materials' },
                { to: '/trust',       label: 'How buying works' },
                ...(user ? [{ to: '/projects',  label: 'My Projects' }] : []),
                ...(user ? [{ to: '/favorites', label: 'Saved Listings' }] : []),
                ...(user ? [{ to: '/alerts',    label: 'Material Alerts' }] : []),
              ]} onNavigate={() => setMobileMenuOpen(false)} />

              <MobileSection title="For suppliers" testId="mobile-suppliers" items={[
                { to: '/suppliers',          label: 'Browse Suppliers' },
                { to: '/auth?mode=register', label: 'Become a Supplier' },
                { to: '/trust',              label: 'Trust & Verification' },
                { to: '/supplier-app',       label: 'Wingman Mobile App' },
                ...(user?.user_type === 'supplier' ? [{ to: '/dashboard',     label: 'Supplier Dashboard' }] : []),
                ...(user?.user_type === 'supplier' ? [{ to: '/trade-exchange', label: 'Trade · Exchange' }] : []),
              ]} onNavigate={() => setMobileMenuOpen(false)} />

              <MobileSection title="For designers" testId="mobile-designers" items={[
                { to: '/sourcing',    label: 'Sourcing Calls' },
                { to: '/inspiration', label: 'Inspiration Gallery' },
                { to: '/revit',       label: 'Revit Plugin' },
                { to: '/sketchup',    label: 'SketchUp Plugin' },
              ]} onNavigate={() => setMobileMenuOpen(false)} />

              <Link
                to="/about"
                className="text-sm font-medium text-muted-foreground hover:text-accent py-2 mt-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>

              {user ? (
                <>
                  <Link
                    to="/profile"
                    className="text-sm font-medium text-muted-foreground hover:text-accent py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="text-sm font-medium text-destructive hover:text-destructive/80 py-2 text-left"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2 pt-2">
                  <Button
                    variant="ghost"
                    onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}
                    className="justify-start"
                  >
                    Sign In
                  </Button>
                  <Button
                    onClick={() => { navigate('/auth?mode=register'); setMobileMenuOpen(false); }}
                    className="btn-gold"
                  >
                    Get Started
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

/**
 * NavMenuTrigger — desktop audience dropdown (Buyers / Suppliers / Designers).
 * MobileSection — same items, rendered as a flat indented list under a header
 * for the slide-out mobile menu (no nested dropdowns on mobile so the whole
 * IA stays one tap deep).
 *
 * Items: `{to, label}` for in-app routes or `{href, label}` for mailto/external.
 */
function NavMenuTrigger({ label, items, testId }) {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="text-sm font-medium text-muted-foreground hover:text-accent transition-colors tracking-wide uppercase font-manrope inline-flex items-center gap-1"
          data-testid={`${testId}-trigger`}
        >
          {label}
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-56 bg-card border-border"
        data-testid={`${testId}-content`}
      >
        {items.map((item) => (
          <DropdownMenuItem
            key={item.to || item.href}
            onClick={() => {
              if (item.href) {
                window.location.href = item.href;
              } else {
                navigate(item.to);
              }
            }}
            data-testid={`${testId}-${(item.to || item.href).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
          >
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


function MobileSection({ title, items, onNavigate, testId }) {
  return (
    <div className="mt-3 pt-3 border-t border-white/5" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{title}</p>
      <div className="flex flex-col">
        {items.map((item) =>
          item.href ? (
            <a
              key={item.href}
              href={item.href}
              className="text-sm font-medium text-muted-foreground hover:text-accent py-1.5"
              onClick={onNavigate}
            >
              {item.label}
            </a>
          ) : (
            <Link
              key={item.to}
              to={item.to}
              className="text-sm font-medium text-muted-foreground hover:text-accent py-1.5"
              onClick={onNavigate}
            >
              {item.label}
            </Link>
          ),
        )}
      </div>
    </div>
  );
}
